/* eslint-disable  @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { CybersourceSessionResponse, CybersourceValidatePayload, MastercardPayload } from '../models/payment.model';
import { PayAtTableNiPayload } from '../models/pay-at-table.model';
import { ApiService } from './api.service';

export interface CybersourceContainers {
  // Where the payment-method buttons render.
  paymentSelection: string;
  // Where the card form renders. Supplying both keeps the whole flow inline;
  // passing only the button selector puts the form in an overlay sidebar
  // instead, which fights our own dialog layer.
  paymentScreen: string;
}

export interface CybersourceHandlers {
  // The widget produced a transient token; the server turns it into money.
  onToken: (transientToken: string) => void;
  onDeclined: () => void;
  onFatalError: () => void;
}

/**
 * Cybersource / Visa Acceptance Unified Checkout — embedded drop-in.
 *
 * The session endpoint returns a capture context (a signed JWT) plus the SDK
 * URL for the venue's host. Card fields stay inside Cybersource's iframes, so
 * no PCI scope for us.
 *
 * Unlike Checkout.com and Adyen there is no npm package: the SDK is a plain
 * <script>. Both the URL and its subresource-integrity hash are issued per
 * account inside the capture context and handed to us by the backend — they are
 * never constructed here. Cybersource serves the file from its own asset host
 * under a signed, per-account path, so a URL built from the API host loads the
 * wrong thing (or nothing).
 *
 * What mount() resolves to depends on the mode the server chose, which arrives
 * on the session response as processing_mode:
 *
 *  - 'complete_mandate': the widget takes the payment itself (running 3DS on
 *    the way) and resolves with the result. Our server then re-fetches that
 *    payment from Cybersource before settling anything.
 *  - 'server_authorize': the widget resolves with a transient token and our
 *    server makes the charge.
 *
 * Either way this page never decides an order is paid — it hands what it got to
 * the backend, which asks Cybersource. We also never call checkout.complete()
 * ourselves; on the complete-mandate path autoProcessing does it, and on the
 * other path completing in the browser is exactly what we are avoiding.
 */
@Injectable({
  providedIn: 'root',
})
export class CybersourceService {
  private apiService = inject(ApiService);
  private loadedUrl: string | null = null;

  private client: any = null;
  private checkout: any = null;

  public getSession(payload: MastercardPayload | PayAtTableNiPayload): Observable<CybersourceSessionResponse> {
    return this.apiService.getCybersourceSession(payload);
  }

  public async mount(containers: CybersourceContainers, session: CybersourceSessionResponse, handlers: CybersourceHandlers): Promise<boolean> {
    const ok = await this.setupScript(session.sdk_url, session.sdk_integrity);
    if (!ok || !(window as any).VAS) {
      return false;
    }

    try {
      // Initialising validates the JWT signature and checks that this page's
      // origin is one of the capture context's targetOrigins — a mismatch
      // throws here rather than rendering a broken widget.
      this.client = await (window as any).VAS.UnifiedCheckout(session.capture_context);

      // autoProcessing decides what mount() hands back, and it must agree with
      // the capture context the server built: with a completeMandate in it the
      // widget completes the payment and returns the result; without one it
      // returns a transient token for our server to charge. Getting this out of
      // step with the server produces a widget that pays and a backend that
      // cannot settle, or the reverse.
      const autoProcessing = session.processing_mode === 'complete_mandate';
      this.checkout = await this.client.createCheckout({ autoProcessing });

      // Embedded mode: both the button list and the card form render inline.
      // mount() with a single selector would put the form in a sidebar overlay,
      // and mount() with no argument puts both there.
      //
      // Deliberately NOT awaited. mount() resolves only once the guest has
      // finished entering a card, so awaiting it here would keep the caller
      // waiting — and the caller hides its loading overlay when we return,
      // which means the spinner would sit on top of the very widget it is
      // waiting for, forever. Resolve as soon as the widget is attached and
      // deliver the token through the handler instead.
      this.checkout
        .mount({
          paymentSelection: `#${containers.paymentSelection}`,
          paymentScreen: `#${containers.paymentScreen}`,
        })
        .then((transientToken: string) => {
          if (transientToken) {
            handlers.onToken(transientToken);
          } else {
            handlers.onFatalError();
          }
        })
        .catch((error: any) => {
          console.error('Cybersource checkout.mount() rejected', error);
          if (error?.name === 'UnifiedCheckoutError') {
            handlers.onDeclined();
          } else {
            handlers.onFatalError();
          }
        });

      return true;
    } catch (error) {
      // Nothing here can be a card decline: no card has been entered yet.
      // UnifiedCheckoutError at init means the capture context is unusable on
      // this page (origin not in targetOrigins, expired or invalid JWT) — and
      // a decline-style remount would just mint another broken session in an
      // endless loop.
      console.error('Cybersource UnifiedCheckout init failed', error);
      handlers.onFatalError();
      return true;
    }
  }

  public unmount(): void {
    // v1 needs both: unmount() hides the UI, destroy() releases it. Skipping
    // the destroys leaks the iframes across a decline-and-retry remount.
    try {
      this.checkout?.unmount();
      this.checkout?.destroy();
      this.client?.destroy();
    } catch {
      // Tearing down an already-torn-down widget must not break the page.
    }
    this.checkout = null;
    this.client = null;
  }

  private setupScript(sdkUrl: string, integrity?: string): Promise<boolean> {
    return new Promise((resolve) => {
      if (!sdkUrl) {
        resolve(false);
        return;
      }
      if (this.loadedUrl === sdkUrl) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = sdkUrl;
      script.type = 'text/javascript';
      // crossOrigin is required for integrity to be enforced at all — without
      // it the browser silently skips the check.
      script.crossOrigin = 'anonymous';
      if (integrity) {
        script.integrity = integrity;
      }
      script.onload = () => {
        this.loadedUrl = sdkUrl;
        resolve(true);
      };
      script.onerror = (event) => {
        console.error('Cybersource SDK script failed to load', sdkUrl, event);
        resolve(false);
      };
      document.body.appendChild(script);
    });
  }

  public validateCybersourceSession(payload: CybersourceValidatePayload): Observable<boolean> {
    return this.apiService.validateCybersourceTransaction(payload).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }
}
